import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { dmClient } from '../../services/dmClient';
import ErrorBoundary from '../ErrorBoundary';
import DenialResolveV2View from './DenialResolveV2View';
// Dev-drawer panels re-exported from V5View (which re-exports from V4View).
import DenialResolveV5View, {
  S2_RootCause as PrimaryDenialDriverPanel,
  S3_LookupTrace as PolicyCodingValidationPanel,
  S4_SourceGraphTrace as EvidenceSourceDetailsPanel,
  S6_DeterministicFindings as ValidatedFindingsPanel,
  S7_SNNFindings as CaseFindingsPanel,
  S8ext_PayerContradictions as PayerContradictionsRawPanel,
  S11_PacketOutputs as PacketOutputsRawPanel,
  S12_HumanReviewFlags as HumanReviewFlagsPanel,
  S13_LearningTags as LearningTagsPanel,
  S13ext_LearningSignal as LearningSignalPanel,
  C6_AuditTrail as AuditTrailPanel,
} from './DenialResolveV5View';
import './DenialsListPage.css';

// Engine selection: prefer the production Denial Resolve v4 (clean-slate
// 2-axis spec-compliant engine, persists a 13-section Case File). Fall back
// to v3 (deterministic + 195-subtype, same node IDs as v2), then v2, then the
// legacy v1 master. v3 is a strict-superset, drop-in compatible v2 engine
// (DenialResolveV2View renders v3 unchanged). v4 has its OWN view that reads
// the persisted Case File from /dm-api/denials/v4/cases/<case_id>.
//
// NAMING: process maps follow `cm_{domain}_{arch}_{layer}_{subject}_{purpose}`
// (see docs/process_map_naming_convention.md). The arrays below carry the
// new canonical name FIRST and the legacy name as a fallback so the rename
// can roll forward without a frontend/backend lockstep deploy.
const V5_PROCESS_NAMES = ['cm_dm_v5_chat_router_resolve'];
const V4_PROCESS_NAMES = ['cm_dm_v4_master_all_families_resolve', 'denial_resolve_v4'];
const V3_PROCESS_NAMES = ['cm_dm_v3_master_all_families_resolve_deprecated', 'denial_resolve_v3'];
const V2_PROCESS_NAMES = ['cm_dm_v2_master_all_families_resolve_deprecated', 'denial_resolve_v2'];
const V1_PROCESS_NAMES = ['cm_dm_v1_master_all_families_orchestrate_deprecated', 'denial_master_orchestrator'];
// Legacy single-name constants retained for any external reference; prefer
// the *_NAMES arrays above.
const V5_PROCESS_NAME = V5_PROCESS_NAMES[0];
const V4_PROCESS_NAME = V4_PROCESS_NAMES[0];
const V3_PROCESS_NAME = V3_PROCESS_NAMES[0];
const V2_PROCESS_NAME = V2_PROCESS_NAMES[0];
const V1_PROCESS_NAME = V1_PROCESS_NAMES[0];
const TERMINAL_STATES = ['completed', 'failed', 'cancelled', 'succeeded', 'error'];
const DONE_STATES = ['completed', 'succeeded'];
const FAILED_STATES = ['failed', 'cancelled', 'error'];
const PROCESSING_STATES = ['running', 'in_progress', 'processing', 'queued', 'pending', 'starting'];
const DASH = '-';

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

const formatCurrency = (v) => {
  if (v === null || v === undefined || v === '') return DASH;
  const n = Number(v);
  if (Number.isNaN(n)) return DASH;
  return n.toLocaleString(undefined, {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
};

function pctStr(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  if (n <= 1) return `${(n * 100).toFixed(0)}%`;
  return `${n.toFixed(0)}%`;
}

function humanArchetype(ak) {
  if (!ak) return '';
  return {
    dx_proc_inconsistency: 'Dx/Procedure inconsistency',
    coding_bundling: 'Coding / bundling (MUE, NCCI)',
    drg_downgrade: 'DRG downgrade',
    medical_necessity: 'Medical necessity (NCD / LCD)',
    cob_crossover: 'COB / payer crossover',
  }[ak] || ak.replace(/_/g, ' ');
}

function humanAction(action) {
  if (!action) return '';
  return {
    file_appeal: 'file an appeal',
    submit_corrected_claim: 'submit a corrected claim',
    submit_provider_query: 'send a provider query',
    human_review: 'send to human review',
    write_off: 'write off',
  }[action] || action.replace(/_/g, ' ');
}

function shortId(id) {
  if (!id) return '';
  const s = String(id);
  return s.length > 12 ? `${s.slice(0, 8)}...` : s;
}

// ---------------------------------------------------------------------------
// Time helpers (used by the run-strip ETA caption)
// ---------------------------------------------------------------------------

// Typical v4 engine run takes ~3 minutes wall-clock from "run" click to
// terminal status (12-family classification + 195-subtype reasoning +
// payer-contradiction reconciliation + appeal packet drafting + persist).
// Used as the fallback denominator when we can't derive an ETA from the
// live progress fraction.
const V4_DEFAULT_TOTAL_SECS = 180;

function formatHms(secs) {
  const s = Math.max(0, Math.round(Number(secs) || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m < 60) return r === 0 ? `${m}m` : `${m}m ${r}s`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm === 0 ? `${h}h` : `${h}h ${rm}m`;
}

function computeEta({ runStartedAt, progressPct, engineDefault = V4_DEFAULT_TOTAL_SECS }) {
  if (!runStartedAt) return { elapsedSecs: 0, expectedTotalSecs: engineDefault, remainingSecs: engineDefault };
  const elapsedMs = Math.max(0, Date.now() - runStartedAt);
  const elapsedSecs = Math.round(elapsedMs / 1000);
  // If we have meaningful progress (>= 5%), use elapsed/progress to project
  // total. Otherwise fall back to the engine default.
  let expectedTotalSecs;
  if (progressPct >= 5) {
    expectedTotalSecs = Math.round(elapsedSecs / (progressPct / 100));
    // Bound the projection: never claim less than current elapsed, never
    // more than 4x the engine default so a stuck early reading doesn't say
    // "12 minutes" when typical runs are 3 minutes.
    expectedTotalSecs = Math.max(elapsedSecs + 5,
                                 Math.min(engineDefault * 4, expectedTotalSecs));
  } else {
    expectedTotalSecs = Math.max(elapsedSecs + 10, engineDefault);
  }
  const remainingSecs = Math.max(0, expectedTotalSecs - elapsedSecs);
  return { elapsedSecs, expectedTotalSecs, remainingSecs };
}

// ---------------------------------------------------------------------------
// FullPageLoader -- centered spinner + caption used while the case detail
// page is hydrating its initial data (case row + v4 case file). Prevents
// the half-rendered "empty header" flash users used to see during the 2-4s
// it took for both fetches to settle.
// ---------------------------------------------------------------------------
function FullPageLoader({ text }) {
  return (
    <div className="denials-fullpage-loader" role="status" aria-live="polite">
      <div className="denials-fullpage-loader__spinner" aria-hidden="true" />
      <div className="denials-fullpage-loader__text">{text || 'Loading...'}</div>
    </div>
  );
}

function truncate(s, n) {
  if (!s) return '';
  const str = String(s);
  return str.length > n ? `${str.slice(0, n - 1).trim()}...` : str;
}

function fmtDateTime(v) {
  if (!v) return DASH;
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleString();
  } catch { return String(v); }
}

function fmtDate(v) {
  if (!v) return DASH;
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleDateString();
  } catch { return String(v); }
}

function pickOutputs(res) {
  if (!res) return {};
  return res.outputs_produced || res.outputs || res.output || res.result || {};
}

function pickInputs(res) {
  if (!res) return {};
  return res.inputs_used || res.inputs || res.input || {};
}

function pickStatus(res) {
  if (!res) return null;
  return res.status || res.execution_status || null;
}

function nodeStatusFromResult(res) {
  if (!res) return 'not-run';
  const s = String(pickStatus(res) || '').toLowerCase();
  if (s === 'completed' || s === 'succeeded' || s === 'success') return 'completed';
  if (s === 'failed' || s === 'error') return 'failed';
  if (s === 'skipped') return 'skipped';
  if (s === 'running' || s === 'active') return 'running';
  return s || (Object.keys(pickOutputs(res)).length > 0 ? 'completed' : 'unknown');
}

function fields(nodeResults, id) {
  return pickOutputs(nodeResults?.[id]);
}

function strengthClass(s) {
  const k = String(s || '').toLowerCase();
  if (k.startsWith('strong')) return 'denials-strength--strong';
  if (k.startsWith('mod'))    return 'denials-strength--mod';
  if (k.startsWith('weak'))   return 'denials-strength--weak';
  return 'denials-strength--none';
}

// ---------------------------------------------------------------------------
// Phase config -- used by the Developer Drawer and Audit Trail summary.
// ---------------------------------------------------------------------------

const TRAIL_PHASES = [
  { id: 'intake',      title: 'Step 1: Understanding the denial',
    nodes: [
      { id: 'intake_lookup',          title: 'Load payload and run-mode settings' },
      { id: 'intake_carc_lookup',     title: 'Deterministic CARC -> denial-type lookup' },
      { id: 'intake_chain_classifier',title: 'Chain reasoner classifier (Engine-backed, fallback)' },
      { id: 'intake_llm_classifier',  title: 'Engine classifier (final fallback)' },
      { id: 'intake_classifier_merge',title: 'Pick the highest-confidence classification' },
      { id: 'intake_classifier',      title: 'Legacy classifier (kept for back-compat)' },
      { id: 'normalize_lookups',      title: 'Resolve CARC / RARC / CPT / ICD codes' },
      { id: 'case_extractor',         title: 'Extract the structured CaseEvidenceFrame' },
    ] },
  { id: 'evidence',    title: 'Step 2: Gathering clinical evidence',
    nodes: [
      { id: 'evidence_requirements_planner', title: 'Decide what evidence the case needs' },
      { id: 'evidence_fetch_ehr',            title: 'Pull labs / vitals / notes from the EHR' },
      { id: 'clinical_timeline_builder',     title: 'Build the clinical timeline' },
    ] },
  { id: 'policy',      title: 'Step 3: Reasoning over policy and coding knowledge',
    nodes: [
      { id: 'policy_graph_query',   title: 'Engine reasoning over the Policy router graph' },
      { id: 'policy_query_builder', title: 'Build a tailored query per denial type' },
      { id: 'archetype_router',     title: 'Pick which specialist sub-process(es) to run' },
    ] },
  { id: 'archetype',   title: 'Step 4: Denial-type specialist deep reasoning',
    nodes: [], nodeMatcher: (id) => id.startsWith('invoke_') },
  { id: 'strategy',    title: 'Step 5: Deciding the correction strategy',
    nodes: [
      { id: 'correction_planner',  title: 'Pick the correction action per issue' },
      { id: 'financial_estimator', title: 'Estimate the recoverable dollars' },
      { id: 'review_decision',     title: 'Auto-approve vs. send for human review' },
    ] },
  { id: 'appeal',      title: 'Step 6: Generating the appeal and submitting',
    nodes: [
      { id: 'appeal_packet_generator', title: 'Compose the appeal letter, arguments, citations' },
      { id: 'submission_router',       title: 'Pick the right payer channel' },
      { id: 'submit_payer_api',        title: 'POST to the payer (or queue for fallback)' },
    ] },
  { id: 'persistence', title: 'Step 7: Persisting outcome and learning',
    nodes: [
      { id: 'learning_signal_emit', title: 'Emit the learning signal' },
      { id: 'persist_outcome',      title: 'Save appeal + signal + submission to the database' },
      { id: 'record_outcome_stub',  title: 'Record the in-memory ledger entry' },
    ] },
];

function phaseTotals(nodeResults, phase) {
  let nodes = [];
  if (phase.nodeMatcher) {
    nodes = Object.keys(nodeResults || {}).filter(phase.nodeMatcher);
  } else {
    nodes = (phase.nodes || []).map((n) => n.id).filter((id) => nodeResults[id]);
  }
  let totalTokens = 0; let totalMs = 0;
  for (const id of nodes) {
    const r = nodeResults[id] || {};
    totalTokens += Number(r.tokens_used) || 0;
    totalMs     += Number(r.duration_ms) || 0;
  }
  return { count: nodes.length, totalTokens, totalMs };
}

// ---------------------------------------------------------------------------
// NodeCard -- per-node engine trace, used by the Developer Drawer at the
// bottom of the page. Hidden by default.
// ---------------------------------------------------------------------------

function NodeCard({ nodeId, title, result }) {
  const status = nodeStatusFromResult(result);
  const outputs = pickOutputs(result);
  const inputs = pickInputs(result);
  const llmPrompt = result?.prompt_rendered || result?.llm_prompt || result?.prompt;
  const llmResponse = result?.llm_response || result?.raw_response;
  const httpResponse = result?.http_response;
  const pythonStdout = result?.python_stdout;
  const pythonStderr = result?.python_stderr;
  const snnTrace = result?.snn_execution_trace;
  const tokensUsed = result?.tokens_used;
  const durationMs = result?.duration_ms;

  let elapsed = null;
  if (typeof durationMs === 'number' && isFinite(durationMs)) {
    elapsed = `${(durationMs / 1000).toFixed(1)}s`;
  } else if (result?.started_at && result?.completed_at) {
    try {
      const dt = (new Date(result.completed_at) - new Date(result.started_at)) / 1000;
      if (isFinite(dt) && dt >= 0) elapsed = `${dt.toFixed(1)}s`;
    } catch { /* ignore */ }
  }

  return (
    <div className={`denials-trail-node denials-trail-node--${status}`}>
      <div className="denials-trail-node__header">
        <span className={`denials-trail-node__status denials-trail-node__status--${status}`}>
          {status}
        </span>
        <span className="denials-trail-node__title">{title || nodeId}</span>
        {result?.execution_type && (
          <span className="denials-trail-node__meta">
            {{ llm: 'engine', python: 'compute', http: 'service', snn: 'reasoning' }[result.execution_type]
              || result.execution_type}
          </span>
        )}
        {tokensUsed != null && Number(tokensUsed) > 0 && (
          <span className="denials-trail-node__meta">
            {Number(tokensUsed).toLocaleString()} tokens
          </span>
        )}
        {elapsed && (
          <span className="denials-trail-node__meta">{elapsed}</span>
        )}
      </div>

      {status === 'not-run' && (
        <div className="denials-trail-node__body denials-cell-muted">
          Did not run in this execution.
        </div>
      )}

      <details className="denials-trail-node__raw">
        <summary>Show inputs, outputs, prompt and engine trace for this step</summary>
        <div className="denials-trail-node__raw-body">
          {Object.keys(inputs).length > 0 && (
            <>
              <h4>Inputs</h4>
              <pre className="denials-output-pre">{JSON.stringify(inputs, null, 2)}</pre>
            </>
          )}
          {Object.keys(outputs).length > 0 && (
            <>
              <h4>Outputs</h4>
              <pre className="denials-output-pre">{JSON.stringify(outputs, null, 2)}</pre>
            </>
          )}
          {llmPrompt && (
            <>
              <h4>Engine prompt</h4>
              <pre className="denials-output-pre">
                {typeof llmPrompt === 'string' ? llmPrompt : JSON.stringify(llmPrompt, null, 2)}
              </pre>
            </>
          )}
          {llmResponse && (
            <>
              <h4>Engine response</h4>
              <pre className="denials-output-pre">
                {typeof llmResponse === 'string' ? llmResponse : JSON.stringify(llmResponse, null, 2)}
              </pre>
            </>
          )}
          {httpResponse && (
            <>
              <h4>HTTP response</h4>
              <pre className="denials-output-pre">
                {typeof httpResponse === 'string' ? httpResponse : JSON.stringify(httpResponse, null, 2)}
              </pre>
            </>
          )}
          {pythonStdout && (<><h4>Python stdout</h4><pre className="denials-output-pre">{pythonStdout}</pre></>)}
          {pythonStderr && (<><h4>Python stderr</h4><pre className="denials-output-pre">{pythonStderr}</pre></>)}
          {snnTrace && (
            <>
              <h4>Engine execution trace</h4>
              <pre className="denials-output-pre">{JSON.stringify(snnTrace, null, 2)}</pre>
            </>
          )}
          {result?.error && (
            <>
              <h4>Error</h4>
              <pre className="denials-output-pre">
                {typeof result.error === 'string' ? result.error : JSON.stringify(result.error, null, 2)}
              </pre>
            </>
          )}
        </div>
      </details>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Aggregations across master + sub-processes
// ---------------------------------------------------------------------------

function collectArchetypeFindings(nodeResults, subprocessOutputs) {
  const out = [];
  Object.entries(nodeResults || {})
    .filter(([k]) => k.startsWith('invoke_'))
    .forEach(([k, r]) => {
      const op = pickOutputs(r);
      const archetype = op.archetype || k.replace(/^invoke_/, '');
      const status = op.status || pickStatus(r);
      const subKey = subprocessOutputs?.[archetype];
      let finding = op.archetype_finding;
      if ((!finding || Object.keys(finding || {}).length === 0) && subKey?.nodeResults) {
        const sFinding = pickOutputs(subKey.nodeResults.archetype_finding);
        finding = sFinding?.archetype_finding || sFinding;
      }
      out.push({ nodeId: k, archetype, status, finding: finding || null, subState: subKey?.state, subNodeResults: subKey?.nodeResults });
    });
  return out;
}

// Unified counter-argument stream feeding the A5 Contradiction Table.
// Two sources, same shape:
//   1. "chain"   - rows from per-archetype archetype_contradiction_llm (chain-anchored,
//                  LLM-graded strength: strong / moderate / weak).
//   2. "finding" - rows from each archetype's archetype_finding.unsupported_issues[]
//                  (the engine concluded the payer's stated reason is unsupported by
//                  the evidence; these are appeal ammunition, not a documentation gap).
function collectContradictions(nodeResults, subprocessOutputs) {
  const rows = [];

  Object.values(subprocessOutputs || {}).forEach((sub) => {
    const ar = sub.archetype;
    const op = pickOutputs(sub.nodeResults?.archetype_contradiction_llm);
    const items = Array.isArray(op.contradictions) ? op.contradictions : [];
    items.forEach((c) => {
      rows.push({
        source: 'chain',
        archetype: ar,
        chain_id: c.chain_id,
        strength: c.strength,
        payer_statement: c.payer_statement,
        contradicting_evidence: c.contradicting_evidence,
      });
    });
  });

  collectArchetypeFindings(nodeResults, subprocessOutputs).forEach((f) => {
    const unsupported = Array.isArray(f.finding?.unsupported_issues)
      ? f.finding.unsupported_issues : [];
    unsupported.forEach((u) => {
      const issue  = typeof u === 'string' ? u : (u.issue || u.text || u.summary);
      const reason = typeof u === 'object' ? (u.reason_unsupported || u.reason || u.detail) : null;
      if (!issue) return;
      rows.push({
        source: 'finding',
        archetype: f.archetype,
        chain_id: null,
        strength: 'counter',
        payer_statement: issue,
        contradicting_evidence: reason,
      });
    });
  });

  return rows;
}

function buildEvidenceTimeline(nodeResults) {
  const ct = fields(nodeResults, 'clinical_timeline_builder');
  const ef = fields(nodeResults, 'evidence_fetch_ehr');
  const bundle = ef.evidence_bundle || ef.bundle || {};

  const events = [];

  const tlSrc = ct.evidence_timeline || ct.timeline || {};
  if (Array.isArray(tlSrc)) {
    tlSrc.forEach((ev) => {
      events.push({
        date: ev.date || ev.event_date,
        source: 'Timeline',
        event: ev.event || ev.summary || ev.description || ev.title,
        value: ev.value || ev.detail || '',
      });
    });
  } else if (tlSrc && typeof tlSrc === 'object') {
    Object.entries(tlSrc).forEach(([date, day]) => {
      if (!day || typeof day !== 'object') return;
      const summary = day.relevance || day.notes_summary || day.summary;
      if (summary) {
        events.push({ date, source: 'Clinical day', event: summary, value: '' });
      }
      ['labs', 'vitals', 'treatments', 'monitoring', 'complications'].forEach((k) => {
        const list = Array.isArray(day[k]) ? day[k] : null;
        if (list && list.length) {
          list.forEach((it) => {
            events.push({
              date,
              source: k.charAt(0).toUpperCase() + k.slice(1),
              event: typeof it === 'string' ? it : (it.name || it.code || k),
              value: typeof it === 'object'
                ? (it.value !== undefined
                  ? `${it.value}${it.unit ? ' ' + it.unit : ''}`
                  : (it.description || ''))
                : '',
            });
          });
        }
      });
    });
  }

  (bundle.labs || []).forEach((l) => {
    events.push({
      date: l.observed_at || l.collected_at || l.PerformedAt || l.SignedAt,
      source: 'Lab',
      event: l.loinc || l.name || l.code || 'Lab result',
      value: l.value !== undefined ? `${l.value}${l.unit ? ' ' + l.unit : ''}` : '',
    });
  });
  (bundle.vitals || []).forEach((v) => {
    events.push({
      date: v.observed_at || v.recorded_at,
      source: 'Vital',
      event: v.name || v.code || 'Vital',
      value: v.value !== undefined ? `${v.value}${v.unit ? ' ' + v.unit : ''}` : '',
    });
  });
  (bundle.notes || bundle.clinical_notes || []).forEach((n) => {
    events.push({
      date: n.SignedAt || n.CreatedAt || n.signed_at || n.created_at,
      source: `Note (${n.AuthorRole || n.author_role || 'clinician'})`,
      event: n.Subject || n.subject || n.NoteType || n.note_type || 'Clinical note',
      value: truncate(n.Body || n.body || '', 220),
    });
  });
  (bundle.orders || []).forEach((o) => {
    events.push({
      date: o.OrderedAt || o.CreatedAt,
      source: `Order (${o.OrderType || 'clinical'})`,
      event: o.Description || o.Code || 'Order',
      value: [o.Dose, o.Route, o.Frequency].filter(Boolean).join(' / '),
    });
  });

  return events
    .filter((e) => e.event)
    .map((e) => ({ ...e, _sortKey: e.date ? new Date(e.date).getTime() : 0 }))
    .sort((a, b) => a._sortKey - b._sortKey);
}

function isCitedByAppeal(timelineEvent, evidenceTable) {
  if (!Array.isArray(evidenceTable) || !timelineEvent.event) return false;
  const evText = String(timelineEvent.event).toLowerCase();
  const evDate = timelineEvent.date ? String(timelineEvent.date).slice(0, 10) : '';
  return evidenceTable.some((row) => {
    const rowText = String(row.evidence || '').toLowerCase();
    const rowDate = String(row.date || '').slice(0, 10);
    if (evDate && rowDate && evDate === rowDate) return true;
    if (rowText && evText && (rowText.includes(evText) || evText.includes(rowText.slice(0, 24)))) return true;
    return false;
  });
}

// ---------------------------------------------------------------------------
// Block A: Case Analysis -- 8 subsections
// ---------------------------------------------------------------------------

function Subsection({ idx, title, children }) {
  return (
    <section className="denials-subsection">
      <h3 className="denials-subsection__title">
        <span className="denials-subsection__idx">{idx}</span>{title}
      </h3>
      <div className="denials-subsection__body">{children}</div>
    </section>
  );
}

function Empty({ children }) {
  return (
    <div className="denials-subsection__empty">
      {children || 'No data available for this section yet.'}
    </div>
  );
}

// Map the internal classification_path tag onto a friendly user label.
// New 3-stage hybrid pipeline writes one of these onto every classification.
function humanClassifierPath(p) {
  if (!p) return null;
  return {
    'deterministic-carc-lookup': 'Deterministic CARC lookup',
    'chain-reasoner':            'Chain reasoner (Engine)',
    'llm-fallback':              'Automated classification (fallback)',
    'legacy-llm-classifier':     'Automated classification (legacy)',
  }[p] || p;
}

function SummarySection({ caseData, nodeResults, appeals, payload, caseId }) {
  const rd  = fields(nodeResults, 'review_decision');
  const cp  = fields(nodeResults, 'correction_planner');
  // New hybrid pipeline writes the final pick onto intake_classifier_merge.
  // Fall back to the legacy intake_classifier node for older executions
  // that ran before the hybrid pipeline was deployed.
  const merge  = fields(nodeResults, 'intake_classifier_merge');
  const legacy = fields(nodeResults, 'intake_classifier');
  const ic = (merge && Object.keys(merge).length > 0) ? merge : legacy;
  const archetypeKey = ic.primary_archetype || ic.archetype
    || caseData?.Archetype || caseData?.archetype || '';
  const arch = humanArchetype(archetypeKey);
  const classifierPath = humanClassifierPath(ic.classification_path);
  const classifierRationale = ic.classification_rationale;
  const conf = rd.final_confidence_pct != null
    ? `${Number(rd.final_confidence_pct).toFixed(0)}%`
    : (rd.overall_confidence != null ? pctStr(rd.overall_confidence) : null);
  const action = cp.overall_action ? humanAction(cp.overall_action) : null;
  const decision = rd.auto_approved === true
    ? 'Auto-approved'
    : (rd.human_review_required === true ? 'Needs human review' : null);
  const savedAt = appeals?.[0]?.CreatedAt;

  const patient = caseData?.PatientId || caseData?.patientId || payload?.patient?.patient_id;
  const payer = caseData?.PayerName || caseData?.payerName || payload?.coverage?.payer_name;
  const claim = caseData?.ClaimNumber || caseData?.claimNumber || payload?.claim_number;

  const sentenceBits = [];
  if (patient && payer) {
    sentenceBits.push(`Patient ${patient} was denied by ${payer}${claim ? ` on claim ${claim}` : ''}.`);
  } else if (claim) {
    sentenceBits.push(`Claim ${claim} was denied.`);
  }
  if (arch) {
    sentenceBits.push(
      `The engine classified this as a ${arch} denial` +
      `${classifierPath ? ` via ${classifierPath.toLowerCase()}` : ''}.`
    );
  }
  if (classifierRationale) {
    sentenceBits.push(`Classifier rationale: ${classifierRationale}`);
  }
  if (action) {
    sentenceBits.push(`Recommended action: ${action}${conf ? ` (engine confidence ${conf})` : ''}.`);
  }
  if (decision) {
    sentenceBits.push(`${decision}${rd.decision_reason ? ` -- ${rd.decision_reason}` : ''}.`);
  }
  if (savedAt) {
    sentenceBits.push(`Engine output first persisted ${fmtDateTime(savedAt)}.`);
  }

  return (
    <Subsection idx="A1" title="Summary">
      {sentenceBits.length > 0 ? (
        <p className="denials-summary-paragraph">{sentenceBits.join(' ')}</p>
      ) : (
        <Empty>The engine has not produced a decision summary for this case yet.</Empty>
      )}
      <div className="denials-summary-chips">
        <Chip label="Case" value={caseData?.ExternalId || caseId} />
        <Chip label="Patient" value={patient} />
        <Chip label="Payer" value={payer} />
        <Chip label="Claim" value={claim} />
        <Chip label="Denial Type" value={arch} />
        <Chip label="Classifier"
              value={classifierPath}
              tone={ic.classification_path === 'deterministic-carc-lookup' ? 'good'
                  : ic.classification_path === 'chain-reasoner' ? 'good'
                  : ic.classification_path === 'llm-fallback' ? 'warn' : null} />
        <Chip label="Confidence" value={conf} tone={confTone(rd.final_confidence_pct)} />
        <Chip label="Decision" value={decision} tone={decision === 'Auto-approved' ? 'good' : 'warn'} />
        <Chip label="Action" value={action} />
      </div>
    </Subsection>
  );
}

function confTone(pct) {
  if (pct == null) return null;
  const n = Number(pct);
  if (n >= 80) return 'good';
  if (n >= 50) return 'warn';
  return 'bad';
}

function Chip({ label, value, tone }) {
  if (value == null || value === '' || value === DASH) return null;
  return (
    <div className={`denials-chip ${tone ? `denials-chip--${tone}` : ''}`}>
      <span className="denials-chip__label">{label}</span>
      <span className="denials-chip__value">{value}</span>
    </div>
  );
}

function FinancialImpactSection({ caseData, nodeResults, payload }) {
  const fe = fields(nodeResults, 'financial_estimator');
  const lineBilled = (payload?.claim_lines || []).reduce(
    (acc, l) => acc + (Number(l.billed_amount) || 0), 0,
  );
  const totalDenied = caseData?.TotalDeniedAmount || caseData?.totalDeniedAmount;
  const hasAny = fe.expected_value != null || fe.gross_disputed != null
    || totalDenied != null || lineBilled > 0;

  if (!hasAny) {
    return (
      <Subsection idx="A2" title="Financial Impact">
        <Empty>The financial estimator has not run for this case yet.</Empty>
      </Subsection>
    );
  }

  return (
    <Subsection idx="A2" title="Financial Impact">
      <div className="denials-financial">
        <div className="denials-financial__hero">
          <div className="denials-financial__hero-label">Expected recovery</div>
          <div className="denials-financial__hero-value">{formatCurrency(fe.expected_value)}</div>
          <div className="denials-financial__hero-sub">
            {fe.historical_win_rate != null
              ? `Based on a ${(Number(fe.historical_win_rate) * 100).toFixed(0)}% historical win rate`
              : 'Win rate unavailable'}
            {fe.priority ? ` -- priority: ${fe.priority}` : ''}
          </div>
        </div>
        <table className="denials-kv-table">
          <tbody>
            <tr><th>Total denied (case)</th><td>{formatCurrency(totalDenied)}</td></tr>
            <tr><th>Sum billed (claim lines)</th><td>{formatCurrency(lineBilled)}</td></tr>
            <tr><th>Gross disputed</th><td>{formatCurrency(fe.gross_disputed)}</td></tr>
            <tr><th>Contract adjusted</th><td>{formatCurrency(fe.contract_adjusted)}</td></tr>
            <tr><th>Historical win rate</th><td>{fe.historical_win_rate != null ? `${(Number(fe.historical_win_rate) * 100).toFixed(1)}%` : DASH}</td></tr>
            <tr><th>Expected recovery</th><td><strong>{formatCurrency(fe.expected_value)}</strong></td></tr>
            <tr><th>Priority</th><td>{fe.priority || DASH}</td></tr>
            {fe.overlap_warning && (
              <tr><th>Overlap warning</th><td className="denials-cell-warn">Yes -- another claim may overlap</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Subsection>
  );
}

function DenialIssuesSection({ nodeResults, payload }) {
  const cp = fields(nodeResults, 'correction_planner');
  const plan = Array.isArray(cp.plan) ? cp.plan : [];
  const denials = Array.isArray(payload?.denials) ? payload.denials : [];

  if (denials.length === 0 && plan.length === 0) {
    return (
      <Subsection idx="A3" title="Denial Issues">
        <Empty>No denial events on this case payload.</Empty>
      </Subsection>
    );
  }

  const rows = denials.length > 0
    ? denials.map((d, i) => {
        const match = plan.find((p) => {
          const issueText = String(p.issue || p.issue_text || '').toLowerCase();
          const note = String(d.payer_note || '').toLowerCase();
          return issueText && note && (issueText.includes(note.slice(0, 24)) || note.includes(issueText.slice(0, 24)));
        }) || plan[i] || {};
        return { denial: d, plan: match, idx: i };
      })
    : plan.map((p, i) => ({ denial: {}, plan: p, idx: i }));

  return (
    <Subsection idx="A3" title="Denial Issues">
      <table className="denials-issues-table">
        <thead>
          <tr>
            <th>Line</th>
            <th>CARC / RARC</th>
            <th>Denied $</th>
            <th>Payer note</th>
            <th>Engine action</th>
            <th>Channel</th>
            <th>Engine rationale</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ denial, plan: p, idx }) => (
            <tr key={idx}>
              <td>{denial.line_no ?? DASH}</td>
              <td>
                <div>{denial.carc || DASH}</div>
                <div className="denials-cell-sub">{denial.rarc || ''}</div>
              </td>
              <td className="denials-cell-num">{formatCurrency(denial.denied_amount)}</td>
              <td>{denial.payer_note || p.issue || DASH}</td>
              <td>
                {p.action
                  ? <span className="denials-action-badge">{humanAction(p.action)}</span>
                  : DASH}
              </td>
              <td>{p.channel || DASH}</td>
              <td>{p.rationale || DASH}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Subsection>
  );
}

function EvidenceTimelineSection({ nodeResults }) {
  const events = useMemo(() => buildEvidenceTimeline(nodeResults), [nodeResults]);
  const evidenceTable = (fields(nodeResults, 'appeal_packet_generator').appeal_packet || {}).evidence_table || [];

  if (events.length === 0) {
    return (
      <Subsection idx="A4" title="Evidence Timeline">
        <Empty>No clinical evidence was returned for this case.</Empty>
      </Subsection>
    );
  }

  return (
    <Subsection idx="A4" title="Evidence Timeline">
      <p className="denials-subsection__hint">
        Rows highlighted in yellow are cited in the appeal packet below.
      </p>
      <table className="denials-timeline-table">
        <thead>
          <tr><th>Date</th><th>Source</th><th>Event</th><th>Value / detail</th><th>Cited</th></tr>
        </thead>
        <tbody>
          {events.map((e, i) => {
            const cited = isCitedByAppeal(e, evidenceTable);
            return (
              <tr key={i} className={cited ? 'denials-row--cited' : ''}>
                <td className="denials-cell-num">{fmtDate(e.date)}</td>
                <td>{e.source}</td>
                <td>{e.event}</td>
                <td>{e.value}</td>
                <td>{cited ? 'Yes' : ''}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Subsection>
  );
}

function ContradictionTableSection({ nodeResults, subprocessOutputs }) {
  const rows = useMemo(
    () => collectContradictions(nodeResults, subprocessOutputs),
    [nodeResults, subprocessOutputs],
  );

  const chainCount   = rows.filter((r) => r.source === 'chain').length;
  const findingCount = rows.filter((r) => r.source === 'finding').length;

  return (
    <Subsection idx="A5" title="Contradiction Table">
      <p className="denials-subsection__hint">
        Every payer denial reason that the engine found counter-evidence for.
        Two row types: <strong>Chain reasoner</strong> rows are anchored to a
        specific reasoning chain (chain id + engine-graded strength); <strong>Finding</strong>
        rows are the denial-type specialist's overall conclusion that the
        payer's stated reason is unsupported by the evidence. Both are
        appeal ammunition.
      </p>
      {rows.length === 0 ? (
        <Empty>No specialist sub-process produced contradictions (none ran, or none found any).</Empty>
      ) : (
        <>
          <table className="denials-contradiction-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Source</th>
                <th>Chain</th>
                <th>Strength</th>
                <th>Payer statement</th>
                <th>Contradicting evidence</th>
                <th>Denial Type</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr
                  key={i}
                  className={strengthClass(r.strength)}
                  id={`contradiction-${r.chain_id || `row-${i}`}`}
                >
                  <td className="denials-cell-num">{i + 1}</td>
                  <td>
                    <span className={`denials-source-tag denials-source-tag--${r.source}`}>
                      {r.source === 'chain' ? 'Chain reasoner' : 'Finding'}
                    </span>
                  </td>
                  <td><code>{r.chain_id || DASH}</code></td>
                  <td>{r.strength || DASH}</td>
                  <td>{r.payer_statement || DASH}</td>
                  <td>{r.contradicting_evidence || DASH}</td>
                  <td>{humanArchetype(r.archetype)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="denials-subsection__hint" style={{ marginTop: 8 }}>
            {chainCount} chain-reasoner row{chainCount === 1 ? '' : 's'},{' '}
            {findingCount} finding-based row{findingCount === 1 ? '' : 's'}.
          </p>
        </>
      )}
    </Subsection>
  );
}

function CodingCorrectionSection({ nodeResults, subprocessOutputs }) {
  const cp = fields(nodeResults, 'correction_planner');
  const plan = Array.isArray(cp.plan) ? cp.plan : [];
  const codingPlan = plan.filter((p) => p.action === 'submit_corrected_claim');
  const enriched = (fields(nodeResults, 'normalize_lookups').enriched_codes) || {};
  const findings = collectArchetypeFindings(nodeResults, subprocessOutputs);
  const modifierSuggestions = findings
    .map((f) => ({ archetype: f.archetype, mod: f.finding?.corrected_modifier_suggestion }))
    .filter((x) => x.mod);

  const hasAny = codingPlan.length > 0 || modifierSuggestions.length > 0;

  return (
    <Subsection idx="A6" title="Coding Correction">
      {!hasAny ? (
        <Empty>The engine did not recommend any coding corrections for this case.</Empty>
      ) : (
        <>
          {codingPlan.length > 0 && (
            <table className="denials-coding-table">
              <thead>
                <tr><th>Issue</th><th>Recommended action</th><th>Channel</th><th>Engine rationale</th></tr>
              </thead>
              <tbody>
                {codingPlan.map((p, i) => (
                  <tr key={i}>
                    <td>{p.issue || DASH}</td>
                    <td><span className="denials-action-badge">{humanAction(p.action)}</span></td>
                    <td>{p.channel || DASH}</td>
                    <td>{p.rationale || DASH}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {modifierSuggestions.length > 0 && (
            <div className="denials-coding-modifiers">
              <h4>Modifier suggestions</h4>
              <ul>
                {modifierSuggestions.map((m, i) => (
                  <li key={i}>
                    <strong>{humanArchetype(m.archetype)}:</strong>{' '}
                    {typeof m.mod === 'string'
                      ? m.mod
                      : JSON.stringify(m.mod)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(enriched.cpt?.length || enriched.icd10cm?.length || enriched.modifier?.length) > 0 && (
            <div className="denials-coding-resolved">
              <h4>Codes resolved by the engine</h4>
              <div className="denials-coding-resolved__grid">
                {enriched.cpt?.length > 0 && <div><span className="denials-tag">CPT</span> {enriched.cpt.map((c) => typeof c === 'string' ? c : (c.code || JSON.stringify(c))).join(', ')}</div>}
                {enriched.hcpcs?.length > 0 && <div><span className="denials-tag">HCPCS</span> {enriched.hcpcs.map((c) => typeof c === 'string' ? c : (c.code || JSON.stringify(c))).join(', ')}</div>}
                {enriched.icd10cm?.length > 0 && <div><span className="denials-tag">ICD-10-CM</span> {enriched.icd10cm.map((c) => typeof c === 'string' ? c : (c.code || JSON.stringify(c))).join(', ')}</div>}
                {enriched.icd10pcs?.length > 0 && <div><span className="denials-tag">ICD-10-PCS</span> {enriched.icd10pcs.map((c) => typeof c === 'string' ? c : (c.code || JSON.stringify(c))).join(', ')}</div>}
                {enriched.modifier?.length > 0 && <div><span className="denials-tag">Modifier</span> {enriched.modifier.map((c) => typeof c === 'string' ? c : (c.code || JSON.stringify(c))).join(', ')}</div>}
              </div>
            </div>
          )}
        </>
      )}
    </Subsection>
  );
}

function MissingDocumentationSection({ nodeResults }) {
  const cp = fields(nodeResults, 'correction_planner');
  const plan = Array.isArray(cp.plan) ? cp.plan : [];
  const providerQueries = plan.filter((p) => p.action === 'submit_provider_query');
  const pkt = (fields(nodeResults, 'appeal_packet_generator').appeal_packet) || {};
  const checklist = Array.isArray(pkt.attachment_checklist) ? pkt.attachment_checklist : [];

  const hasAny = providerQueries.length > 0 || checklist.length > 0;

  return (
    <Subsection idx="A7" title="Missing Documentation">
      {!hasAny ? (
        <Empty>No documentation gaps were identified for this case.</Empty>
      ) : (
        <>
          {checklist.length > 0 && (
            <>
              <h4>What we need to attach</h4>
              <ul className="denials-doc-list">
                {checklist.map((c, i) => (
                  <li key={i}>{typeof c === 'string' ? c : (c.label || c.text || JSON.stringify(c))}</li>
                ))}
              </ul>
            </>
          )}
          {providerQueries.length > 0 && (
            <>
              <h4>Provider queries to send</h4>
              <ul className="denials-doc-list">
                {providerQueries.map((p, i) => (
                  <li key={i}>
                    <strong>{p.issue}</strong>
                    {p.rationale ? <span> -- {p.rationale}</span> : null}
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </Subsection>
  );
}

function RecommendedActionSection({ nodeResults }) {
  const cp = fields(nodeResults, 'correction_planner');
  const rd = fields(nodeResults, 'review_decision');
  const sr = fields(nodeResults, 'submission_router');
  const overall = cp.overall_action;
  const top = (Array.isArray(cp.plan) && cp.plan[0]) || null;

  if (!overall && !top && !rd.auto_approved && !rd.human_review_required) {
    return (
      <Subsection idx="A8" title="Recommended Action">
        <Empty>The engine has not concluded a recommended action yet.</Empty>
      </Subsection>
    );
  }

  const decisionLabel = rd.auto_approved === true
    ? 'Auto-approved'
    : (rd.human_review_required === true ? 'Needs human review' : 'Decision pending');
  const decisionTone = rd.auto_approved === true ? 'good' : (rd.human_review_required === true ? 'warn' : 'muted');

  return (
    <Subsection idx="A8" title="Recommended Action">
      <div className={`denials-recommendation denials-recommendation--${decisionTone}`}>
        <div className="denials-recommendation__decision">{decisionLabel}</div>
        <div className="denials-recommendation__headline">
          {overall ? `Engine recommends to ${humanAction(overall)}.` : 'Engine has no overall action.'}
        </div>
        {top && (
          <div className="denials-recommendation__top">
            <strong>Next step:</strong> {humanAction(top.action) || DASH}
            {top.channel ? <> via <code>{top.channel}</code></> : null}
            {top.rationale ? <div className="denials-cell-sub">{top.rationale}</div> : null}
          </div>
        )}
        <div className="denials-recommendation__route">
          <strong>Submission route:</strong> {sr.channel ? `${sr.channel}${sr.endpoint_path ? ` (${sr.endpoint_path})` : ''}` : 'not selected'}
        </div>
        {rd.decision_reason && (
          <div className="denials-recommendation__why">
            <strong>Why this decision:</strong> {rd.decision_reason}
            {rd.final_confidence_pct != null && (
              <> &middot; engine confidence <strong>{Number(rd.final_confidence_pct).toFixed(0)}%</strong></>
            )}
            {rd.effective_threshold_pct != null && (
              <> &middot; threshold <strong>{rd.effective_threshold_pct === -1 || rd.effective_threshold_pct === '-1' ? 'disabled' : `${Number(rd.effective_threshold_pct).toFixed(0)}%`}</strong></>
            )}
          </div>
        )}
      </div>
    </Subsection>
  );
}

function CaseAnalysisBlock(props) {
  return (
    <section className="denials-block denials-block--analysis">
      <header className="denials-block__header">
        <h2>A. Case Analysis</h2>
        <p>Everything the engine concluded about this denial -- verify the recommendation before sending the appeal.</p>
      </header>
      <SummarySection {...props} />
      <FinancialImpactSection {...props} />
      <DenialIssuesSection {...props} />
      <EvidenceTimelineSection {...props} />
      <ContradictionTableSection {...props} />
      <CodingCorrectionSection {...props} />
      <MissingDocumentationSection {...props} />
      <RecommendedActionSection {...props} />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Block C: Appeal-Ready Packet -- 6 subsections
// ---------------------------------------------------------------------------

function AppealLetterSection({ nodeResults }) {
  const pkt = (fields(nodeResults, 'appeal_packet_generator').appeal_packet) || {};
  const letter = pkt.appeal_letter;
  return (
    <Subsection idx="C1" title="Appeal Letter">
      {letter ? (
        <>
          <div className="denials-appeal-letter">{letter}</div>
          {pkt.requested_resolution && (
            <p className="denials-subsection__hint" style={{ marginTop: 10 }}>
              <strong>Requested resolution:</strong> {pkt.requested_resolution}
            </p>
          )}
        </>
      ) : (
        <Empty>No appeal letter has been generated yet.</Empty>
      )}
    </Subsection>
  );
}

function EvidenceTableSection({ nodeResults }) {
  const pkt = (fields(nodeResults, 'appeal_packet_generator').appeal_packet) || {};
  const rows = Array.isArray(pkt.evidence_table) ? pkt.evidence_table : [];
  return (
    <Subsection idx="C2" title="Evidence Table">
      {rows.length === 0 ? (
        <Empty>The appeal packet does not yet include an evidence table.</Empty>
      ) : (
        <table className="denials-evidence-table">
          <thead>
            <tr><th>Date</th><th>Evidence</th><th>Supports chain</th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td className="denials-cell-num">{r.date || DASH}</td>
                <td>{r.evidence || DASH}</td>
                <td>
                  {r.supports_chain_id
                    ? <a href={`#contradiction-${r.supports_chain_id}`}><code>{r.supports_chain_id}</code></a>
                    : DASH}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Subsection>
  );
}

function PolicyCrosswalkSection({ nodeResults }) {
  const pkt = (fields(nodeResults, 'appeal_packet_generator').appeal_packet) || {};
  const rows = Array.isArray(pkt.policy_crosswalk) ? pkt.policy_crosswalk : [];
  return (
    <Subsection idx="C3" title="Policy Crosswalk">
      {rows.length === 0 ? (
        <Empty>No policy crosswalk arguments were produced.</Empty>
      ) : (
        <table className="denials-crosswalk-table">
          <thead>
            <tr><th>Policy reference</th><th>Policy clause</th><th>Appeal argument</th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td><code>{r.policy_graph_ref || DASH}</code></td>
                <td>{r.clause || DASH}</td>
                <td>{r.appeal_argument_paragraph || DASH}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Subsection>
  );
}

function CodingCorrectionAnalysisSection({ nodeResults, subprocessOutputs }) {
  const cp = fields(nodeResults, 'correction_planner');
  const plan = Array.isArray(cp.plan) ? cp.plan : [];
  const codingPlan = plan.filter((p) => p.action === 'submit_corrected_claim');
  const findings = collectArchetypeFindings(nodeResults, subprocessOutputs);
  const mods = findings
    .map((f) => ({ archetype: f.archetype, mod: f.finding?.corrected_modifier_suggestion }))
    .filter((x) => x.mod);
  const enriched = (fields(nodeResults, 'normalize_lookups').enriched_codes) || {};

  if (codingPlan.length === 0 && mods.length === 0) {
    return (
      <Subsection idx="C4" title="Coding Correction Analysis">
        <Empty>No coding-correction narrative is required for this appeal.</Empty>
      </Subsection>
    );
  }

  const codeList = [
    enriched.cpt?.length ? `CPT: ${enriched.cpt.join(', ')}` : null,
    enriched.icd10cm?.length ? `ICD-10-CM: ${enriched.icd10cm.join(', ')}` : null,
    enriched.modifier?.length ? `modifiers: ${enriched.modifier.join(', ')}` : null,
  ].filter(Boolean).join('; ');

  return (
    <Subsection idx="C4" title="Coding Correction Analysis">
      <div className="denials-narrative">
        {codingPlan.map((p, i) => (
          <p key={i}>
            <strong>{p.issue}</strong> -- the engine recommends to{' '}
            {humanAction(p.action)} via {p.channel || 'the default channel'}.{' '}
            {p.rationale}
          </p>
        ))}
        {mods.map((m, i) => (
          <p key={`mod-${i}`}>
            <strong>{humanArchetype(m.archetype)}:</strong> recommended modifier change ={' '}
            {typeof m.mod === 'string' ? m.mod : JSON.stringify(m.mod)}.
          </p>
        ))}
        {codeList && (
          <p>
            <strong>Codes the engine resolved on this claim:</strong> {codeList}.
          </p>
        )}
      </div>
    </Subsection>
  );
}

function AttachmentChecklistSection({ nodeResults, caseId }) {
  const pkt = (fields(nodeResults, 'appeal_packet_generator').appeal_packet) || {};
  const items = Array.isArray(pkt.attachment_checklist) ? pkt.attachment_checklist : [];
  const storageKey = `denials.attachments.${caseId}`;

  const [done, setDone] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || '{}'); }
    catch { return {}; }
  });
  const toggle = (i) => {
    const next = { ...done, [i]: !done[i] };
    setDone(next);
    try { localStorage.setItem(storageKey, JSON.stringify(next)); }
    catch { /* non-fatal */ }
  };

  if (items.length === 0) {
    return (
      <Subsection idx="C5" title="Attachment Checklist">
        <Empty>No attachments are required for this appeal.</Empty>
      </Subsection>
    );
  }

  const doneCount = items.filter((_, i) => done[i]).length;

  return (
    <Subsection idx="C5" title="Attachment Checklist">
      <p className="denials-subsection__hint">
        {doneCount} of {items.length} attached.
        Toggles are saved locally so you can resume this checklist later.
      </p>
      <ul className="denials-checklist">
        {items.map((c, i) => {
          const label = typeof c === 'string' ? c : (c.label || c.text || JSON.stringify(c));
          return (
            <li key={i} className={done[i] ? 'denials-checklist__item--done' : ''}>
              <label>
                <input
                  type="checkbox"
                  checked={!!done[i]}
                  onChange={() => toggle(i)}
                />
                <span>{label}</span>
              </label>
            </li>
          );
        })}
      </ul>
    </Subsection>
  );
}

function AuditTrailSection({ execState, nodeResults, appeals, caseData }) {
  const events = useMemo(() => {
    const out = [];
    if (execState?.started_at) {
      out.push({ at: execState.started_at, label: 'Engine run started', detail: shortId(execState.execution_id) });
    }
    TRAIL_PHASES.forEach((phase) => {
      const totals = phaseTotals(nodeResults, phase);
      if (totals.count === 0) return;
      out.push({
        at: null,
        label: phase.title,
        detail: `${totals.count} node${totals.count === 1 ? '' : 's'}` +
                (totals.totalTokens > 0 ? ` -- ${totals.totalTokens.toLocaleString()} tokens` : '') +
                (totals.totalMs > 0    ? ` -- ${(totals.totalMs / 1000).toFixed(1)}s` : ''),
      });
    });
    const rd = fields(nodeResults, 'review_decision');
    if (rd.auto_approved !== undefined || rd.human_review_required !== undefined) {
      out.push({
        at: null,
        label: rd.auto_approved ? 'Engine auto-approved the appeal' : 'Engine routed to human review',
        detail: rd.decision_reason || '',
      });
    }
    const po = fields(nodeResults, 'persist_outcome');
    if (po.persisted) {
      out.push({
        at: null,
        label: 'Outcome persisted',
        detail: [
          po.appeal_id     ? 'DenialAppeal'         : null,
          po.signal_id     ? 'DenialLearningSignal' : null,
          po.submission_id ? 'AppealSubmission'     : null,
        ].filter(Boolean).join(', ') + (po.case_status ? ` -- case status now "${po.case_status}"` : ''),
      });
    }
    (appeals || []).forEach((a) => {
      out.push({
        at: a.CreatedAt,
        label: `Appeal saved (${a.Status || 'unknown'})`,
        detail: `${a.AppealLevel || ''}${a.Confidence != null ? ` -- confidence ${(Number(a.Confidence) * 100).toFixed(0)}%` : ''}` +
                `${a.ExpectedRecovery != null ? ` -- expected ${formatCurrency(a.ExpectedRecovery)}` : ''}` +
                `${a.HumanReviewRequired ? ' -- HITL flagged' : ''}`,
      });
    });
    if (execState?.completed_at) {
      out.push({ at: execState.completed_at, label: `Engine run ${execState.status || 'completed'}`, detail: '' });
    }
    return out;
  }, [execState, nodeResults, appeals]);

  if (events.length === 0) {
    return (
      <Subsection idx="C6" title="Audit Trail">
        <Empty>No engine activity has been recorded for this case yet.</Empty>
      </Subsection>
    );
  }

  return (
    <Subsection idx="C6" title="Audit Trail">
      <ul className="denials-audit">
        {events.map((e, i) => (
          <li key={i} className="denials-audit-event">
            <span className="denials-audit-event__time">{e.at ? fmtDateTime(e.at) : ''}</span>
            <span className="denials-audit-event__label">{e.label}</span>
            {e.detail && <span className="denials-audit-event__detail">{e.detail}</span>}
          </li>
        ))}
      </ul>
      {caseData?.ExecutionId && (
        <p className="denials-subsection__hint" style={{ marginTop: 8 }}>
          Linked engine execution: <code>{caseData.ExecutionId}</code>
        </p>
      )}
    </Subsection>
  );
}

function AppealPacketBlock(props) {
  return (
    <section className="denials-block denials-block--packet">
      <header className="denials-block__header">
        <h2>C. Appeal-Ready Packet</h2>
        <p>What gets sent to the payer. Review and edit before submission.</p>
      </header>
      <AppealLetterSection {...props} />
      <EvidenceTableSection {...props} />
      <PolicyCrosswalkSection {...props} />
      <CodingCorrectionAnalysisSection {...props} />
      <AttachmentChecklistSection {...props} />
      <AuditTrailSection {...props} />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Developer drawer -- all the per-node engine trace and raw outputs, hidden
// behind a single toggle for debuggability.
// ---------------------------------------------------------------------------

function _hasAny(v) {
  if (v === null || v === undefined) return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v).length > 0;
  return true;
}

function InternalPanel({ title, hint, children }) {
  return (
    <details className="denials-dev-drawer__internal">
      <summary>
        <span className="denials-dev-drawer__internal-title">{title}</span>
        {hint && <span className="denials-dev-drawer__internal-hint">{hint}</span>}
      </summary>
      <div className="denials-dev-drawer__internal-body">{children}</div>
    </details>
  );
}

function DeveloperDrawer({ nodeResults, subprocessOutputs, execState, caseFile }) {
  const hasNodes = nodeResults && Object.keys(nodeResults).length > 0;
  const hasCaseFile = caseFile && typeof caseFile === 'object' && Object.keys(caseFile).length > 0;
  const waitingGates = Object.keys(execState?.waiting_gates || {});
  const hasWaitingGates = waitingGates.length > 0;
  if (!hasNodes && !hasCaseFile && !hasWaitingGates) return null;

  const phases = TRAIL_PHASES.map((phase) => {
    if (phase.nodeMatcher) {
      const matches = Object.keys(nodeResults || {}).filter(phase.nodeMatcher).sort()
        .map((nid) => ({
          id: nid,
          title: `${humanArchetype(nid.replace(/^invoke_/, ''))} -- specialist sub-process`,
        }));
      return { ...phase, nodes: matches };
    }
    return phase;
  });

  return (
    <details className="denials-dev-drawer">
      <summary>
        <span className="denials-dev-drawer__title">Developer / Audit Details</span>
        <span className="denials-dev-drawer__hint">
          Engine internals, per-node traces, raw outputs. Hidden by default; for debugging and audit.
        </span>
      </summary>
      <div className="denials-dev-drawer__body">
        {hasCaseFile && (
          <section className="denials-dev-drawer__phase">
            <h4>Engine internals (v4 Case File)</h4>

            {hasWaitingGates && (
              <InternalPanel title="Waiting gates"
                hint="Engine choke-points awaiting input. Not a human-review need on their own.">
                <ul>
                  {waitingGates.map((g) => (<li key={g}><code>{g}</code></li>))}
                </ul>
              </InternalPanel>
            )}

            {_hasAny(caseFile.root_cause) && (
              <InternalPanel title="Primary Denial Driver"
                hint="Root cause: primary + secondary causes, payer rationale, CM interpretation.">
                <PrimaryDenialDriverPanel rc={caseFile.root_cause} />
              </InternalPanel>
            )}

            {_hasAny(caseFile.lookup_trace) && (
              <InternalPanel title="Policy / Coding Validation"
                hint="Every deterministic lookup fired, with inputs and results.">
                <PolicyCodingValidationPanel rows={caseFile.lookup_trace} />
              </InternalPanel>
            )}

            {_hasAny(caseFile.source_graph_trace) && (
              <InternalPanel title="Evidence Source Details"
                hint="Verbatim policy clauses retrieved from the routed source graphs.">
                <EvidenceSourceDetailsPanel rows={caseFile.source_graph_trace} />
              </InternalPanel>
            )}

            {_hasAny(caseFile.deterministic_findings) && (
              <InternalPanel title="Validated Findings"
                hint="Rule-by-rule pass / fail / unknown.">
                <ValidatedFindingsPanel rows={caseFile.deterministic_findings} />
              </InternalPanel>
            )}

            {_hasAny(caseFile.snn_reasoning_findings) && (
              <InternalPanel title="Case Findings"
                hint="Natural-language conclusions grounded in a finding or source clause.">
                <CaseFindingsPanel rows={caseFile.snn_reasoning_findings} />
              </InternalPanel>
            )}

            {_hasAny(caseFile.payer_contradictions) && (
              <InternalPanel title="Payer Contradictions (raw)"
                hint="Full contradiction table, including verbatim source clauses.">
                <PayerContradictionsRawPanel rows={caseFile.payer_contradictions} />
              </InternalPanel>
            )}

            {_hasAny(caseFile.packet_outputs) && (
              <InternalPanel title="Packet Outputs (raw)"
                hint="Raw packet payloads (appeal letter, corrected claim, documentation request).">
                <PacketOutputsRawPanel pkt={caseFile.packet_outputs} />
              </InternalPanel>
            )}

            {_hasAny(caseFile.audit_trail) && (
              <InternalPanel title="Audit Trail"
                hint="The named algorithmic steps the engine took for this case.">
                <AuditTrailPanel steps={caseFile.audit_trail} />
              </InternalPanel>
            )}

            {_hasAny(caseFile.human_review_flags) && (
              <InternalPanel title="Human Review Flags"
                hint="Automation gates triggered by the engine.">
                <HumanReviewFlagsPanel flags={caseFile.human_review_flags} />
              </InternalPanel>
            )}

            {_hasAny(caseFile.learning_tags) && (
              <InternalPanel title="Learning Tags"
                hint="Payer, policy clauses, evidence types, and outcome path for the feedback loop.">
                <LearningTagsPanel tags={caseFile.learning_tags} />
              </InternalPanel>
            )}

            {_hasAny(caseFile.learning_signal) && (
              <InternalPanel title="Learning Signal"
                hint="Chains fired, supported/unsupported issues, and outcome for the feedback loop.">
                <LearningSignalPanel ls={caseFile.learning_signal} />
              </InternalPanel>
            )}
          </section>
        )}

        {hasNodes && phases.map((phase) => {
          const nodesInPhase = phase.nodes
            .map((n) => ({ ...n, result: nodeResults[n.id] }))
            .filter((n) => n.result);
          if (nodesInPhase.length === 0) return null;
          return (
            <section key={phase.id} className="denials-dev-drawer__phase">
              <h4>{phase.title}</h4>
              <div className="denials-trail-phase__trace-body">
                {nodesInPhase.map((n) => (
                  <NodeCard key={n.id} nodeId={n.id} title={n.title} result={n.result} />
                ))}
              </div>
            </section>
          );
        })}
        {Object.keys(subprocessOutputs || {}).length > 0 && (
          <section className="denials-dev-drawer__phase">
            <h4>Sub-process raw outputs</h4>
            {Object.entries(subprocessOutputs).map(([ak, sub]) => (
              <details key={ak} className="denials-dev-drawer__sub">
                <summary>{humanArchetype(ak)} ({shortId(sub.execId)})</summary>
                <pre className="denials-output-pre">{JSON.stringify({
                  state: sub.state,
                  nodeResults: sub.nodeResults,
                  error: sub.error,
                }, null, 2)}</pre>
              </details>
            ))}
          </section>
        )}
        {execState && (
          <section className="denials-dev-drawer__phase">
            <h4>Raw final_outputs</h4>
            <pre className="denials-output-pre">{JSON.stringify(execState.final_outputs || {}, null, 2)}</pre>
          </section>
        )}
      </div>
    </details>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

function DenialCaseRunPage({ forceEngine = null }) {
  const { caseId } = useParams();
  const location = useLocation();

  const [caseData, setCaseData] = useState(null);
  const [loadingCase, setLoadingCase] = useState(false);
  const [caseError, setCaseError] = useState(null);

  const [masterProcessId, setMasterProcessId] = useState(null);
  const [masterError, setMasterError] = useState(null);
  const [activeEngine, setActiveEngine] = useState(null); // 'v1' | 'v2' | 'v3' | 'v4' | 'v5'
  const [selectedEngine, setSelectedEngine] = useState(
    forceEngine || location.state?.engine || 'v5'
  );
  const [v4CaseFile, setV4CaseFile] = useState(null);
  const [v4CaseFileLoading, setV4CaseFileLoading] = useState(false);
  const [v4CaseFileError, setV4CaseFileError] = useState(null);

  // Values the user supplies when the v2 minimal-input contract interrupts the
  // run asking for missing fields. Merged into the payload on the next run.
  const [providedInputs, setProvidedInputs] = useState({});

  const [executionId, setExecutionId] = useState(null);
  const [execState, setExecState] = useState(null);
  const [execUnavailable, setExecUnavailable] = useState(false);
  const [polling, setPolling] = useState(false);
  const [starting, setStarting] = useState(false);
  const [runError, setRunError] = useState(null);
  // Wall-clock ms when the current/last run was kicked off. Used to render
  // an "elapsed / expected remaining" caption under the progress bar so the
  // specialist knows roughly how long to wait.
  const [runStartedAt, setRunStartedAt] = useState(null);
  // Re-render every second while a run is in flight so the elapsed-time
  // caption keeps ticking even if the engine status payload doesn't change.
  const [, setNowTick] = useState(0);
  const autoStartedRef = useRef(false);

  const [appeals, setAppeals] = useState([]);
  const [appealsError, setAppealsError] = useState(null);

  const [nodeResults, setNodeResults] = useState({});
  const [nodeResultsError, setNodeResultsError] = useState(null);

  const [subprocessOutputs, setSubprocessOutputs] = useState({});
  const [subprocessError, setSubprocessError] = useState(null);

  const [runMode, setRunMode] = useState('Auto');
  const [confidenceThreshold, setConfidenceThreshold] = useState(80);

  const pollRef = useRef(null);

  const loadCase = useCallback(async () => {
    setLoadingCase(true);
    setCaseError(null);
    try {
      const resp = await dmClient.get(`/denials/${caseId}`);
      setCaseData(resp.data?.data || resp.data);
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || 'Failed to load case';
      setCaseError(msg);
    } finally {
      setLoadingCase(false);
    }
  }, [caseId]);

  const lookupMaster = useCallback(async () => {
    setMasterError(null);
    try {
      const resp = await dmClient.get('/process/definitions', {
        params: { limit: 200, status: 'active' },
      });
      const defs = resp.data || [];
      // byName tries the canonical (new) name first and falls back to the
      // legacy name. Lets the frontend deploy ahead of (or behind) the DB
      // rename without breaking.
      const byName = (name) => defs.find((d) => (d.name || '').trim() === name);
      const byNames = (names) => {
        for (const n of names) {
          const m = byName(n);
          if (m) return m;
        }
        return undefined;
      };
      // Prefer the user-selected engine from dropdown. Fall back to
      // forceEngine (URL-based) or auto-detect best available.
      const v5 = byNames(V5_PROCESS_NAMES);
      const v4 = byNames(V4_PROCESS_NAMES);
      const v3 = byNames(V3_PROCESS_NAMES);
      const v2 = byNames(V2_PROCESS_NAMES);
      const v1 = byNames(V1_PROCESS_NAMES);
      let chosen;
      let engineLabel;
      const pick = forceEngine || selectedEngine;
      if (pick === 'v5') {
        if (!v5) {
          setMasterError(
            `Engine "${V5_PROCESS_NAME}" is not published. Publish the v5 chat-router process map first.`
          );
          return;
        }
        chosen = v5;
        engineLabel = 'v5';
      } else if (pick === 'v4') {
        if (!v4) {
          setMasterError(
            `Engine "${V4_PROCESS_NAME}" is not published. Run `
            + 'tools/denial_v4/process_map/publish_v4_bundle.py first.'
          );
          return;
        }
        chosen = v4;
        engineLabel = 'v4';
      } else {
        chosen = v5 || v4 || v3 || v2 || v1;
        if (!chosen) {
          setMasterError(
            `No denial orchestrator found ("${V5_PROCESS_NAME}", "${V4_PROCESS_NAME}", "${V3_PROCESS_NAME}", "${V2_PROCESS_NAME}", or "${V1_PROCESS_NAME}"). `
            + 'Publish the v5 chat-router bundle, v4 bundle (tools/denial_v4/process_map/publish_v4_bundle.py), '
            + 'the v3 bundle (tools/denial_v3/process_map/publish_v3_bundle.py), '
            + 'the v2 bundle (tools/denial_v2/publish_resolve_bundle.py), or run '
            + 'tools/denial/create_denial_processes.py first.'
          );
          return;
        }
        engineLabel = v5 ? 'v5' : (v4 ? 'v4' : (v3 ? 'v3' : (v2 ? 'v2' : 'v1')));
      }
      setMasterProcessId(chosen.process_id);
      setActiveEngine(engineLabel);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.response?.data?.message || e.message || 'GraphBuilder unreachable';
      setMasterError(msg);
    }
  }, [forceEngine, selectedEngine]);

  const loadAppeals = useCallback(async () => {
    setAppealsError(null);
    try {
      const resp = await dmClient.get(`/denials/${caseId}/appeals`);
      const data = resp.data?.data || {};
      setAppeals(data.items || []);
    } catch (e) {
      const msg = e?.response?.data?.message || e.message;
      if (msg && !/404/.test(String(msg))) setAppealsError(msg);
      setAppeals([]);
    }
  }, [caseId]);

  // Fetch the persisted 13-section v4 Case File for this case. Called when
  // the active engine is v4 and whenever a new v4 run completes.
  const loadV4CaseFile = useCallback(async () => {
    if (!caseId) return;
    setV4CaseFileLoading(true);
    setV4CaseFileError(null);
    try {
      const resp = await dmClient.get(`/denials/v4/cases/${caseId}`);
      const data = resp.data?.data || null;
      if (data && data.case_file) {
        setV4CaseFile(data.case_file);
      } else {
        setV4CaseFile(null);
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        // No persisted Case File yet; treat as empty state, not an error.
        setV4CaseFile(null);
      } else {
        const msg = e?.response?.data?.message || e.message;
        setV4CaseFileError(msg || 'failed to load v4 Case File');
      }
    } finally {
      setV4CaseFileLoading(false);
    }
  }, [caseId]);

  // Fetch every sub-process state + node-results in parallel for archetypes
  // that actually ran. Skipped invoke_* nodes have execution_id=null so they
  // are simply omitted.
  const loadSubprocessOutputs = useCallback(async (results) => {
    if (!results) { setSubprocessOutputs({}); return; }
    const invokes = Object.entries(results)
      .filter(([k]) => k.startsWith('invoke_'))
      .map(([k, r]) => {
        const out = pickOutputs(r);
        return {
          nodeId: k,
          archetype: out.archetype || k.replace(/^invoke_/, ''),
          execId: out.execution_id,
        };
      })
      .filter((x) => x.execId);
    if (invokes.length === 0) {
      setSubprocessOutputs({});
      setSubprocessError(null);
      return;
    }
    setSubprocessError(null);
    try {
      const tasks = invokes.flatMap(({ archetype, execId, nodeId }) => [
        dmClient.get(`/process/status/${execId}`)
          .then((r) => ({ archetype, execId, nodeId, kind: 'state', data: r.data }))
          .catch((e) => ({ archetype, execId, nodeId, kind: 'state', error: e.message })),
        dmClient.get(`/process/status/${execId}/node-results`)
          .then((r) => ({ archetype, execId, nodeId, kind: 'nodes', data: r.data }))
          .catch((e) => ({ archetype, execId, nodeId, kind: 'nodes', error: e.message })),
      ]);
      const responses = await Promise.all(tasks);
      const map = {};
      for (const r of responses) {
        if (!map[r.archetype]) {
          map[r.archetype] = { archetype: r.archetype, execId: r.execId, nodeId: r.nodeId };
        }
        if (r.kind === 'state' && r.data) map[r.archetype].state = r.data;
        if (r.kind === 'nodes' && r.data) map[r.archetype].nodeResults = r.data;
        if (r.error) {
          map[r.archetype].error = (map[r.archetype].error || '') + r.error + ' ';
        }
      }
      setSubprocessOutputs(map);
    } catch (e) {
      setSubprocessError(e.message || 'Failed to load sub-process outputs');
      setSubprocessOutputs({});
    }
  }, []);

  const loadNodeResults = useCallback(async (execId) => {
    if (!execId) return;
    setNodeResultsError(null);
    try {
      const resp = await dmClient.get(`/process/status/${execId}/node-results`);
      const data = resp.data || {};
      setNodeResults(data);
      loadSubprocessOutputs(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      if (msg && !/404/.test(String(msg))) setNodeResultsError(msg);
      setNodeResults({});
      setSubprocessOutputs({});
    }
  }, [loadSubprocessOutputs]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
    setPolling(false);
  }, []);

  const pollOnce = useCallback(async (execId) => {
    try {
      const resp = await dmClient.get(`/process/status/${execId}`);
      const state = resp.data;
      setExecState(state);
      const status = (state?.status || '').toLowerCase();
      if (TERMINAL_STATES.includes(status)) {
        stopPolling();
        loadAppeals();
        loadCase();
        loadNodeResults(execId);
        loadV4CaseFile();
        return;
      }
      if (status === 'paused' && state?.waiting_gates &&
          Object.keys(state.waiting_gates).length > 0) {
        stopPolling();
        return;
      }
      pollRef.current = setTimeout(() => pollOnce(execId), 5000);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Status poll failed';
      setRunError(msg);
      stopPolling();
    }
  }, [stopPolling, loadAppeals, loadCase, loadNodeResults, loadV4CaseFile]);

  const hydrateFromCase = useCallback(async () => {
    const execId = caseData?.ExecutionId || caseData?.executionId;
    if (!execId) return;
    setExecutionId(execId);
    try {
      const resp = await dmClient.get(`/process/status/${execId}`);
      setExecState(resp.data);
      setExecUnavailable(false);
      const status = (resp.data?.status || '').toLowerCase();
      if (PROCESSING_STATES.includes(status)) {
        setPolling(true);
        pollRef.current = setTimeout(() => pollOnce(execId), 5000);
      }
    } catch (_e) {
      // The linked engine execution could not be fetched (e.g. it 404s because
      // it expired or was never persisted under this id). Flag it so the UI can
      // explain the situation instead of spinning on "Processing" forever.
      setExecUnavailable(true);
    }
    loadNodeResults(execId);
  }, [caseData, loadNodeResults, pollOnce]);

  useEffect(() => { loadCase(); }, [loadCase]);
  useEffect(() => { lookupMaster(); }, [lookupMaster]);
  // The standalone /denials/<caseId>/appeals fetch is only consumed by the
  // legacy v1 CaseAnalysisBlock / AppealPacketBlock. The v4 view reads the
  // latest appeal directly from caseFile.appeal_record (joined server-side by
  // the v4 GET route). Skip the call on v4 to avoid the redundant -- and
  // currently 401-ing -- request that produced "Appeals: Failed to list
  // appeals for case ..." in the page error banner.
  useEffect(() => {
    if (activeEngine && activeEngine !== 'v4' && activeEngine !== 'v5') loadAppeals();
  }, [activeEngine, loadAppeals]);
  useEffect(() => { hydrateFromCase(); }, [hydrateFromCase]);
  // Load the persisted v4 Case File on mount (covers the "open an already-
  // processed v4 case" path). It's also re-loaded after each terminal run.
  useEffect(() => {
    if (activeEngine === 'v4' || activeEngine === 'v5') loadV4CaseFile();
  }, [activeEngine, loadV4CaseFile]);

  // Keep `runStartedAt` in sync with the engine's own `started_at` when we
  // re-attach to a still-in-flight execution after a page refresh (we
  // didn't set it locally in that case, but the engine knows when it began).
  useEffect(() => {
    if (!runStartedAt && execState?.started_at) {
      const t = Date.parse(execState.started_at);
      if (Number.isFinite(t)) setRunStartedAt(t);
    }
  }, [execState?.started_at, runStartedAt]);

  // 1Hz tick while a run is in flight so the elapsed-time caption under
  // the progress bar keeps updating even when the engine status payload
  // hasn't changed yet. The setter is a no-op on cleanup.
  useEffect(() => {
    if (!starting && !polling) return undefined;
    const id = setInterval(() => setNowTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [starting, polling]);

  // Hydrate provided_inputs from the persisted case Payload so a page refresh
  // (or Re-run Engine after refresh) doesn't lose values the user already
  // supplied at the v2 interrupt prompt (e.g. clinical_documentation).
  useEffect(() => {
    if (!caseData) return;
    const payload = caseData.Payload || caseData.payload || {};
    const persisted = payload?.provided_inputs;
    if (persisted && typeof persisted === 'object' && Object.keys(persisted).length > 0) {
      setProvidedInputs((prev) => ({ ...persisted, ...(prev || {}) }));
    }
  }, [caseData]);

  const buildInitialInputs = useCallback((extraProvided) => {
    if (!caseData) return null;
    const payload = caseData.Payload || caseData.payload || {};
    const enriched = JSON.parse(JSON.stringify(payload || {}));
    enriched.patient = {
      ...(enriched.patient || {}),
      patient_id: caseData.PatientId || caseData.patientId,
    };
    enriched.encounter = {
      ...(enriched.encounter || {}),
      encounter_id: caseData.EncounterId || caseData.encounterId,
    };
    if (runMode) enriched.RunMode = runMode;
    if (confidenceThreshold !== '' && confidenceThreshold !== null
        && confidenceThreshold !== undefined) {
      enriched.ConfidenceThreshold = Number(confidenceThreshold);
    }
    const inputs = {
      denial_payload: enriched,
      case_id: caseData.ExternalId || caseData.externalId || caseId,
      case_uuid: caseId,
    };
    if (runMode) inputs.run_mode = runMode;
    if (confidenceThreshold !== '' && confidenceThreshold !== null
        && confidenceThreshold !== undefined) {
      inputs.confidence_threshold = Number(confidenceThreshold);
    }
    // v2 interrupt-for-input: user-supplied values are merged into the payload
    // by the input_contract_preflight node (keyed by contract entry id).
    const merged = { ...(providedInputs || {}), ...(extraProvided || {}) };
    if (Object.keys(merged).length > 0) {
      inputs.provided_inputs = merged;
    }
    return inputs;
  }, [caseData, caseId, runMode, confidenceThreshold, providedInputs]);

  const startRun = useCallback(async (extraProvided) => {
    if (!masterProcessId) {
      setRunError('Master orchestrator not available yet.');
      return;
    }
    const inputs = buildInitialInputs(extraProvided);
    if (!inputs) {
      setRunError('Case payload not loaded yet.');
      return;
    }
    // Clear stale state from any previous run so the UI doesn't briefly show
    // the prior "2 nodes done" result while the new execution boots up.
    // Also clear the persisted v4 Case File so the page doesn't keep
    // rendering the OLD result alongside the in-flight "Processing..." card
    // -- the new case file will be re-fetched once the run reaches a
    // terminal status (see pollOnce).
    stopPolling();
    setRunError(null);
    setExecutionId(null);
    setExecState(null);
    setExecUnavailable(false);
    setNodeResults({});
    setSubprocessOutputs({});
    setSubprocessError(null);
    setNodeResultsError(null);
    setV4CaseFile(null);
    setV4CaseFileError(null);
    setRunStartedAt(Date.now());
    setStarting(true);
    try {
      const resp = await dmClient.post('/process/execute', {
        process_id: masterProcessId,
        inputs,
        execution_mode: 'full',
      });
      const state = resp.data;
      setExecutionId(state.execution_id);
      setExecState(state);
      const status = (state?.status || '').toLowerCase();
      if (!TERMINAL_STATES.includes(status)) {
        setPolling(true);
        pollRef.current = setTimeout(() => pollOnce(state.execution_id), 4000);
      }
      try {
        await dmClient.patch(`/denials/${caseId}`, {
          executionId: state.execution_id,
          status: 'processing',
        });
      } catch (_e) { /* non-fatal */ }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.response?.data?.message || e.message || 'Failed to start';
      setRunError(msg);
    } finally {
      setStarting(false);
    }
  }, [masterProcessId, buildInitialInputs, caseId, pollOnce, stopPolling]);

  // v2 interrupt-for-input: the preflight node reports contract_satisfied=false
  // and an input_request when hard-required fields are missing. Surface it so
  // the run page can collect the values and re-run with provided_inputs merged.
  const contractRequest = useMemo(() => {
    if (activeEngine !== 'v2' && activeEngine !== 'v3') return null;
    const pf = pickOutputs(nodeResults?.input_contract_preflight);
    const fromFinal = (execState?.final_outputs || {});
    const satisfied = pf.contract_satisfied != null
      ? pf.contract_satisfied
      : fromFinal.contract_satisfied;
    const request = pf.input_request || fromFinal.input_request || null;
    if (satisfied === false && request) {
      return { needs_input: true, ...request };
    }
    return null;
  }, [activeEngine, nodeResults, execState]);

  const provideAndRerun = useCallback(async (values) => {
    const safe = (values && typeof values === 'object') ? values : {};
    const next = { ...(providedInputs || {}), ...safe };
    setProvidedInputs(next);
    // Persist the supplied values into DenialCase.Payload->'provided_inputs'
    // so the v2 preflight can hydrate them on the next run and so the user
    // doesn't have to retype them after a page refresh. Non-fatal on error;
    // the engine fix means provided_inputs in the request body is also enough
    // on its own.
    if (Object.keys(safe).length > 0) {
      try {
        await dmClient.patch(`/denials/${caseId}`, { providedInputs: safe });
      } catch (_e) { /* non-fatal */ }
    }
    startRun(safe);
  }, [providedInputs, startRun, caseId]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  // Auto-start the engine once when arriving at a not-yet-run case (e.g. just
  // submitted from Data Integrations). Re-opening a case that already has an
  // execution simply hydrates/polls it instead of launching a new run.
  useEffect(() => {
    if (autoStartedRef.current) return;
    if (!caseData || !masterProcessId || masterError) return;
    if (polling || starting) return;
    const caseExecId = caseData.ExecutionId || caseData.executionId;
    if (executionId || caseExecId) return;
    const caseStatus = (caseData.Status || caseData.status || 'new').toLowerCase();
    if (TERMINAL_STATES.includes(caseStatus)) return;
    const wantAutostart = location.state?.autostart === true || caseStatus === 'new';
    if (!wantAutostart) return;
    autoStartedRef.current = true;
    startRun();
  }, [caseData, masterProcessId, masterError, executionId, polling, starting, location.state, startRun]);

  const statusKey = (execState?.status || 'pending').toLowerCase();
  // The process engine reports `progress` as a percentage (0..100). Some older
  // paths emit a 0..1 fraction, so normalize both and clamp to [0,100].
  const rawProgress = Number(execState?.progress ?? 0) || 0;
  const progressPct = Math.max(
    0,
    Math.min(100, Math.round(rawProgress > 0 && rawProgress <= 1 ? rawProgress * 100 : rawProgress)),
  );

  // A case can be "already processed" at the case level (its workflow ran and
  // a terminal/outcome status was persisted) even when the live engine
  // execution can no longer be fetched. Trust the case status in that case so
  // we show the result instead of spinning on "Processing" against a stale id.
  const caseStatusKey = String(caseData?.Status || caseData?.status || '').toLowerCase();
  const CASE_PROCESSED_STATES = ['pending_review', 'appealed', 'closed', 'completed', 'succeeded'];
  const caseIsProcessed = CASE_PROCESSED_STATES.includes(caseStatusKey);

  const hasExecution = Boolean(executionId || execState);
  // Bug fix: a re-run on an already-processed case must immediately show
  // "Processing..." instead of the stale DB status. `starting || polling`
  // wins over `caseIsProcessed` so a fresh button click never displays
  // "Processed" while the engine is actively re-running.
  const liveRerun = starting || polling;
  const liveProcessing = liveRerun
    || (Boolean(execState) && PROCESSING_STATES.includes(statusKey));
  const isProcessing = liveRerun || (liveProcessing && !caseIsProcessed);
  const isDone = !isProcessing
    && ((hasExecution && DONE_STATES.includes(statusKey)) || caseIsProcessed);
  const isFailed = !isProcessing && !caseIsProcessed
    && hasExecution && FAILED_STATES.includes(statusKey);

  // ---------------------------------------------------------------------
  // RCM-friendly status hierarchy.
  //
  // Replaces the old "Processed / Awaiting human review / Failed" mix
  // with one of:
  //   - Processing...           (engine actively running)
  //   - Processing Failed       (execution errored)
  //   - Missing Documentation   (human review flag with missing_* reason)
  //   - Human Review Required   (engine genuinely could not solve)
  //   - Action Required: <verb> (engine solved + user must file/submit)
  //   - Resolved                (no follow-up required)
  //   - Queued                  (initial state with no execution)
  //
  // Importantly: `findings_merge_gate` and similar engine choke-points are
  // NOT human-review needs and are no longer surfaced as a status. The
  // "Human Review Required" label is reserved for cases the engine could
  // not confidently solve.
  // ---------------------------------------------------------------------
  const recAction = (v4CaseFile?.recommended_action?.action || '').toLowerCase();
  const reviewFlags = Array.isArray(v4CaseFile?.human_review_flags)
    ? v4CaseFile.human_review_flags : [];
  const hasMissingDocsFlag = reviewFlags.some((f) => {
    const r = String(f?.reason || '').toLowerCase();
    return r.startsWith('missing_') || r.includes('missing');
  });
  const engineCannotSolve = (v4CaseFile?.decision_summary?.human_review_required
      && (recAction === 'human_review' || recAction === 'human_review_required'))
    || recAction === 'human_review'
    || recAction === 'human_review_required';
  const isAppealReady = (
    ['file_appeal', 'appeal'].includes(recAction)
    || v4CaseFile?.decision_summary?.route === 'medical_necessity_appeal'
  ) && v4CaseFile?.case_resolution_summary_ui?.denial_mitigation_found;
  const actionVerb = ({
    file_appeal: 'File appeal',
    appeal: 'File appeal',
    submit_corrected_claim: 'Submit corrected claim',
    corrected_claim: 'Submit corrected claim',
    documentation_submission: 'Submit documentation',
    payer_follow_up: 'Follow up with payer',
    cob_resubmission: 'Resubmit with COB proof',
    auth_correction: 'Correct authorization',
    coding_review: 'Send for coding review',
    clinical_review: 'Send for clinical review',
    underpayment_dispute: 'Dispute underpayment',
    write_off: 'Write off',
    patient_responsibility: 'Bill patient responsibility',
  })[recAction] || null;

  let caseStatus;
  if (isProcessing) {
    caseStatus = { key: 'processing', label: 'Processing\u2026', tone: 'warn' };
  } else if (isFailed) {
    caseStatus = { key: 'failed', label: 'Processing Failed', tone: 'bad' };
  } else if (isAppealReady) {
    caseStatus = { key: 'appeal_ready', label: 'Appeal Ready', tone: 'good' };
  } else if (hasMissingDocsFlag) {
    caseStatus = { key: 'missing_docs', label: 'Missing Documentation', tone: 'warn' };
  } else if (engineCannotSolve) {
    caseStatus = { key: 'human_review', label: 'Human Review Required', tone: 'bad' };
  } else if (actionVerb && (isDone || caseIsProcessed || v4CaseFile)) {
    caseStatus = { key: 'action_required', label: `Action Required: ${actionVerb}`, tone: 'good' };
  } else if (isDone) {
    caseStatus = { key: 'resolved', label: 'Resolved', tone: 'good' };
  } else if (hasExecution) {
    caseStatus = { key: 'pending', label: execState?.status || 'pending', tone: null };
  } else {
    caseStatus = { key: 'queued', label: 'Queued', tone: null };
  }
  const friendlyStatus = caseStatus.label;
  const statusPillKey = caseStatus.key;

  const nodeResultsEmpty = !nodeResults || Object.keys(nodeResults).length === 0;
  // Show the processing card whenever the engine is actively running,
  // regardless of whether stale node results / a previous case file exist.
  // Pre-refactor we only showed it when node results were empty, which
  // meant a re-run on an already-processed case quietly kept the prior
  // result visible while the new run was in flight, making it look like
  // nothing was happening.
  const showProcessingCard = isProcessing || Boolean(masterError);
  // The case is processed but we couldn't load its engine run AND we have no
  // node results to render -- tell the user plainly and offer a re-run.
  const showExecUnavailable = execUnavailable && caseIsProcessed
    && nodeResultsEmpty && !isProcessing && !masterError;

  const payload = caseData?.Payload || caseData?.payload || {};

  // Show a single centered loader while the page is hydrating its initial
  // data. The header used to render with an empty `caseData?.ExternalId`
  // (showing the raw UUID) for the 2-4s the case fetch and the v4 case
  // file fetch were in flight in parallel.
  const initialLoading = !caseData && loadingCase;
  const v4InitialLoading = (activeEngine === 'v4' || activeEngine === 'v5')
    && v4CaseFileLoading
    && !v4CaseFile
    && !v4CaseFileError;
  if (initialLoading || v4InitialLoading) {
    return <FullPageLoader text="Loading case resolution..." />;
  }

  return (
    <div className="denials-detail">
      <div className="denials-detail__breadcrumb" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Link to="/denials">Denials</Link> &nbsp;/&nbsp;
        <span>{caseData?.ExternalId || caseData?.externalId || caseId}</span>
        <Link
          to={`/denials/${caseId}/lookups`}
          style={{ marginLeft: 'auto', fontWeight: 600 }}
          title="Manage lookup reference data scoped to this case"
        >
          Case Lookups →
        </Link>
      </div>

      <div className="denials-detail__header">
        <div>
          <h1>{caseData?.ExternalId || caseData?.externalId || 'Denial Case'}</h1>
          <p style={{ margin: 0, color: '#6b7280', fontSize: 13 }}>
            {payload.summary || caseData?.Description || ''}
          </p>
        </div>
        <div className="denials-actions denials-run-controls">
          <label className="denials-field" style={{ minWidth: 110 }}>
            <span>Run mode</span>
            <select
              value={runMode}
              onChange={(e) => setRunMode(e.target.value)}
              disabled={polling}
            >
              <option value="Auto">Auto</option>
              <option value="HITL">HITL</option>
            </select>
          </label>
          <label className="denials-field" style={{ minWidth: 130 }}
                 title="Confidence floor (0..100) for auto-approval. -1 bypasses the gate.">
            <span>Confidence threshold %</span>
            <input
              type="number"
              min="-1"
              max="100"
              step="1"
              value={confidenceThreshold}
              onChange={(e) => setConfidenceThreshold(e.target.value)}
              disabled={polling || runMode === 'HITL'}
              style={{
                padding: '6px 10px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: 13,
                width: 90,
              }}
            />
          </label>
          <button
            className="denials-btn denials-btn--primary"
            onClick={() => startRun()}
            disabled={!masterProcessId || polling || starting}
          >
            {isProcessing
              ? 'Running...'
              : hasExecution
                ? 'Re-run Engine'
                : 'Run Denial Recovery Engine'}
          </button>
        </div>
      </div>

      {(caseError || masterError || runError || nodeResultsError || appealsError || subprocessError) && (
        <div className="denials-alert denials-alert--error">
          {caseError && <div>Case: {caseError}</div>}
          {masterError && <div>GraphBuilder: {masterError}</div>}
          {runError && <div>Run: {runError}</div>}
          {nodeResultsError && <div>Node results: {nodeResultsError}</div>}
          {appealsError && <div>Appeals: {appealsError}</div>}
          {subprocessError && <div>Sub-processes: {subprocessError}</div>}
        </div>
      )}

      {/* Status banner: prominent single-line status that the specialist
          reads first. Mirrors the mockup -- the status (e.g. "Human Review
          Required") gets its own band above the technical run-strip. */}
      <div className={`denials-status-banner denials-status-banner--${statusPillKey}${caseStatus.tone ? ` denials-status-banner--tone-${caseStatus.tone}` : ''}`}>
        <span className="denials-status-banner__dot" aria-hidden="true" />
        <span className="denials-status-banner__label">{friendlyStatus}</span>
      </div>

      <div className="denials-run-strip">
        <div className="denials-progress" title={`${progressPct}%`}>
          <span style={{ width: `${progressPct}%` }} />
        </div>
        <span className="denials-run-strip__progress-text">
          {progressPct}% &middot; {(execState?.completed_nodes || []).length} nodes done
        </span>
        {isProcessing && (() => {
          const eta = computeEta({ runStartedAt, progressPct });
          return (
            <span className="denials-run-strip__eta" title={`Started ${runStartedAt ? new Date(runStartedAt).toLocaleTimeString() : 'recently'}`}>
              Elapsed {formatHms(eta.elapsedSecs)} &middot; expected ~{formatHms(eta.expectedTotalSecs)} total (~{formatHms(eta.remainingSecs)} left)
            </span>
          );
        })()}
        <span className="denials-run-strip__meta">
          {activeEngine && (
            <span className="denials-engine-badge">
              Engine: {activeEngine === 'v5'
                ? 'Resolve v5 (Chat Router)'
                : (activeEngine === 'v4'
                  ? 'Resolve v4'
                  : (activeEngine === 'v3'
                    ? 'Resolve v3'
                    : (activeEngine === 'v2' ? 'Resolve v2' : 'v1')))}
            </span>
          )}
        </span>
        {execState?.failed_nodes?.length > 0 && (
          <span className="denials-cell-warn">
            Failed: {execState.failed_nodes.join(', ')}
          </span>
        )}
      </div>

      {showProcessingCard && (
        <div className={`denials-processing-card${masterError ? ' denials-processing-card--error' : ''}`}>
          {masterError ? (
            <>
              <div className="denials-processing-card__title">Engine unavailable</div>
              <p className="denials-processing-card__text">
                The Denial Recovery Engine could not be reached, so this claim
                hasn&apos;t been processed yet. {masterError}
              </p>
            </>
          ) : (
            <>
              <div className="denials-processing-card__spinner" aria-hidden="true" />
              <div className="denials-processing-card__title">
                Processing this claim&hellip; {progressPct}%
              </div>
              <p className="denials-processing-card__text">
                The engine is analyzing the denial, gathering clinical and coding
                evidence, and drafting the appeal. This page updates automatically &mdash;
                results will appear below as soon as they&apos;re ready.
              </p>
              {(() => {
                const eta = computeEta({ runStartedAt, progressPct });
                return (
                  <p className="denials-processing-card__eta">
                    Elapsed <strong>{formatHms(eta.elapsedSecs)}</strong> &middot;
                    {' '}expected total <strong>~{formatHms(eta.expectedTotalSecs)}</strong>
                    {' '}({formatHms(eta.remainingSecs)} remaining)
                  </p>
                );
              })()}
            </>
          )}
        </div>
      )}

      {showExecUnavailable && (
        <div className="denials-processing-card denials-processing-card--error">
          <div className="denials-processing-card__title">Result not available to display</div>
          <p className="denials-processing-card__text">
            This case was already processed, but its engine run could not be
            loaded (it may have expired). Re-run the engine to regenerate the
            full result below.
          </p>
        </div>
      )}

      {/* Result body: hidden while an engine run is actively in flight so
          the user sees only the processing card until the new run finishes.
          The previous v4 case file is also cleared in startRun() so a stale
          result cannot leak through during re-runs. */}
      {!isProcessing && (
        (activeEngine === 'v4' || activeEngine === 'v5') ? (
          <ErrorBoundary
            title="Denial Resolve v5 Case File could not be displayed"
            fallbackData={{ case_file: v4CaseFile, payload }}
          >
            <DenialResolveV5View
              caseFile={v4CaseFile}
              loading={v4CaseFileLoading}
              error={v4CaseFileError}
              onRefresh={loadV4CaseFile}
            />
          </ErrorBoundary>
        ) : (activeEngine === 'v2' || activeEngine === 'v3') ? (
          <ErrorBoundary
            title={`Denial Resolve ${activeEngine} result could not be displayed`}
            fallbackData={{ final_outputs: execState?.final_outputs, payload }}
          >
            <DenialResolveV2View
              nodeResults={nodeResults}
              contractRequest={contractRequest}
              onProvideInputs={provideAndRerun}
              submitting={starting || polling}
            />
          </ErrorBoundary>
        ) : (
          <>
            <ErrorBoundary
              title="Case Analysis could not be displayed"
              fallbackData={{ final_outputs: execState?.final_outputs, payload }}
            >
              <CaseAnalysisBlock
                caseData={caseData}
                nodeResults={nodeResults}
                subprocessOutputs={subprocessOutputs}
                appeals={appeals}
                payload={payload}
                caseId={caseId}
              />
            </ErrorBoundary>

            <ErrorBoundary
              title="Appeal-Ready Packet could not be displayed"
              fallbackData={fields(nodeResults, 'appeal_packet_generator').appeal_packet || execState?.final_outputs}
            >
              <AppealPacketBlock
                caseData={caseData}
                nodeResults={nodeResults}
                subprocessOutputs={subprocessOutputs}
                appeals={appeals}
                execState={execState}
                caseId={caseId}
              />
            </ErrorBoundary>
          </>
        )
      )}

      <ErrorBoundary title="Developer trace could not be displayed">
        <DeveloperDrawer
          nodeResults={nodeResults}
          subprocessOutputs={subprocessOutputs}
          execState={execState}
          caseFile={v4CaseFile}
        />
      </ErrorBoundary>

    </div>
  );
}

export default DenialCaseRunPage;
